import { createPlugin } from '@customerio/dryad';
import type { ValidationIssue } from '@customerio/dryad';
import { ConditionType } from './condition-types';
import { MESSAGE_TYPE, decodeBitmapName, encodeBitmapName } from '../../codecs/named-bitmap';
import type { NamedBitmapFilter, NotFilter } from '../../codecs/wire-types';
import { DEFAULT_CONSENT_ACTION, CONSENT_QUALIFIER_FIELDS, consentActionOf } from '../../rows/consent-types';
import type { ConsentConditionState, ConsentQualifierField } from '../../rows/consent-types';
import { isRecord } from '@/utils/objects';

// Bitmap names are `cns-<action>[-<field>_<value>]`, minted by services'
// consentQualifierIndexes (core/events/index.go). Don't reuse
// namedBitmapToCondition here: it appends `_*` to a name without an
// underscore, rewriting `cns-voided`.

const CONSENT_BITMAP_PREFIX = 'cns';

const INDEXABLE_VALUE = /^[a-z0-9_]+$/;

function namedBitmapNameOf(json: unknown): string | null {
  if (!isRecord(json) || !isRecord(json.named_bitmap)) {
    return null;
  }
  const { type, name } = json.named_bitmap;
  if (type !== MESSAGE_TYPE || typeof name !== 'string') {
    return null;
  }
  return name;
}

function unwrapConsentBitmap(json: unknown): { name: string; negated: boolean } | null {
  const direct = namedBitmapNameOf(json);
  if (direct != null) {
    return { name: direct, negated: false };
  }
  const negated = isRecord(json) ? namedBitmapNameOf(json.not) : null;
  return negated == null ? null : { name: negated, negated: true };
}

function decodeName(name: string): string | null {
  try {
    return decodeBitmapName(name);
  } catch {
    return null;
  }
}

function parseQualifier(qualifier: string): { field: ConsentQualifierField; value: string } | null {
  for (const field of CONSENT_QUALIFIER_FIELDS) {
    const prefix = `${field}_`;
    if (!qualifier.startsWith(prefix)) {
      continue;
    }
    const value = qualifier.slice(prefix.length);
    return INDEXABLE_VALUE.test(value) ? { field, value } : null;
  }
  return null;
}

function decodeConsentBitmap(json: unknown): ConsentConditionState | null {
  const unwrapped = unwrapConsentBitmap(json);
  if (unwrapped == null) {
    return null;
  }

  const decoded = decodeName(unwrapped.name);
  if (decoded == null || !decoded.startsWith(`${CONSENT_BITMAP_PREFIX}-`)) {
    return null;
  }

  const body = decoded.slice(CONSENT_BITMAP_PREFIX.length + 1);
  const separator = body.indexOf('-');
  const action = consentActionOf(separator === -1 ? body : body.slice(0, separator));
  if (action == null) {
    return null;
  }

  if (separator === -1) {
    return { action, field: null, value: null, inverse: unwrapped.negated };
  }

  const qualifier = parseQualifier(body.slice(separator + 1));
  if (qualifier == null) {
    return null;
  }

  return { action, field: qualifier.field, value: qualifier.value, inverse: unwrapped.negated };
}

function serializeConsentBitmap(state: ConsentConditionState): NamedBitmapFilter | NotFilter {
  const qualifier = state.field != null && state.value != null ? `-${state.field}_${state.value}` : '';
  const leaf: NamedBitmapFilter = {
    named_bitmap: {
      type: MESSAGE_TYPE,
      name: encodeBitmapName(`${CONSENT_BITMAP_PREFIX}-${state.action}${qualifier}`),
    },
  };

  return state.inverse ? { not: leaf } : leaf;
}

export function validateConsentCondition(state: ConsentConditionState): Required<ValidationIssue>[] {
  if (state.field != null && (state.value == null || state.value === '')) {
    return [{ message: 'Select a value for this condition.', field: 'value' }];
  }
  return [];
}

export const peopleFilterConsentPlugin = createPlugin({
  type: ConditionType.Consent,
  label: 'Consent',
  parse: decodeConsentBitmap,
  serialize: serializeConsentBitmap,
  validate: validateConsentCondition,
  createDefault: (): ConsentConditionState => ({
    action: DEFAULT_CONSENT_ACTION,
    field: null,
    value: null,
    inverse: false,
  }),
});
